/** Length and content rules for a launch. Byte limits are UTF-8 bytes, as the program and Metaplex count them. */
import { MEDIA_MAX_BYTES, METADATA_BUDGET_BYTES, NAME_MAX_BYTES, SYMBOL_MAX_BYTES } from '@/api/types';

const enc = new TextEncoder();
export const utf8Bytes = (s: string) => enc.encode(s).length;

export type Check = { ok: true } | { ok: false; message: string };
const ok: Check = { ok: true };
const fail = (message: string): Check => ({ ok: false, message });

export function checkName(name: string): Check {
  const n = name.trim();
  if (!n) return fail('Give your coin a name.');
  const b = utf8Bytes(n);
  if (b > NAME_MAX_BYTES) return fail(`Name is ${b} bytes; the limit is ${NAME_MAX_BYTES}.`);
  return ok;
}

export function checkSymbol(symbol: string): Check {
  const s = symbol.trim();
  if (!s) return fail('Add a ticker.');
  if (/\s/.test(s)) return fail('Tickers cannot contain spaces.');
  const b = utf8Bytes(s);
  if (b > SYMBOL_MAX_BYTES) return fail(`Ticker is ${b} bytes; the limit is ${SYMBOL_MAX_BYTES}.`);
  return ok;
}

/** Bytes the launch transaction spends on metadata: name + symbol + uri. */
export const metadataBytes = (name: string, symbol: string, uri: string) => utf8Bytes(name.trim()) + utf8Bytes(symbol.trim()) + utf8Bytes(uri);

/** A launch with a dev buy fits one transaction only when name + symbol + uri <= 144 bytes. */
export function checkMetadataBudget(name: string, symbol: string, uri: string): Check {
  const b = metadataBytes(name, symbol, uri);
  if (b > METADATA_BUDGET_BYTES) {
    return fail(`Name, ticker and metadata link use ${b} bytes; a launch fits one transaction only up to ${METADATA_BUDGET_BYTES}. Shorten the name or ticker by ${b - METADATA_BUDGET_BYTES} bytes.`);
  }
  return ok;
}

export type SocialKind = 'twitter' | 'telegram' | 'website';

const HOSTS: Record<Exclude<SocialKind, 'website'>, string[]> = {
  twitter: ['x.com', 'twitter.com', 'www.x.com', 'www.twitter.com', 'mobile.twitter.com'],
  telegram: ['t.me', 'telegram.me', 'www.t.me'],
};

/** Empty is fine (optional). Otherwise an https URL; X and Telegram links must point at their hosts. */
export function checkSocial(kind: SocialKind, value: string): Check {
  const v = value.trim();
  if (!v) return ok;
  let u: URL;
  try {
    u = new URL(v);
  } catch {
    return fail('Enter a full link starting with https://');
  }
  if (u.protocol !== 'https:') return fail('Links must start with https://');
  if (kind !== 'website' && !HOSTS[kind].includes(u.hostname.toLowerCase())) {
    return fail(kind === 'twitter' ? 'Use an x.com or twitter.com link.' : 'Use a t.me link.');
  }
  if (v.length > 200) return fail('Links are limited to 200 characters.');
  return ok;
}

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const;

export function checkImageFile(file: { type: string; size: number }): Check {
  if (!(IMAGE_TYPES as readonly string[]).includes(file.type)) return fail('Use a JPG, PNG, GIF or WebP image.');
  if (file.size > MEDIA_MAX_BYTES) return fail(`Images are limited to 15 MB (this one is ${(file.size / 1024 / 1024).toFixed(1)} MB).`);
  if (file.size === 0) return fail('That file is empty.');
  return ok;
}

export const DESCRIPTION_MAX = 1000;

export function checkDescription(d: string): Check {
  return d.length > DESCRIPTION_MAX ? fail(`Descriptions are limited to ${DESCRIPTION_MAX} characters.`) : ok;
}

/** Base58 public key shape (32 bytes encode to 32..44 characters). */
export const looksLikeAddress = (s: string) => /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(s.trim());
